import { useState } from 'react';
import { HandKicker } from '../HandKicker';
import { MugshotEditor } from '../mugshot/MugshotEditor';
import { PhaseTag } from '../PhaseTag';
import { RecruitTile } from './RecruitTile';
import { SlotCard } from './SlotCard';
import { gridColsFor, type SuspectBoardProps } from './types';
import { WantedTile } from './WantedTile';

/**
 * Polaroid roster pinned to a `<Stage tone='board'>`. Slot interactivity
 * flows from each slot's own callbacks: `onMugshotChange` makes the
 * polaroid clickable, `onRemove` adds the corner ×, and `onNameChange`
 * unlocks renaming inside the editor. The lobby and standalone setup
 * share this surface; only the slot wiring differs at the call site.
 */
export const SuspectBoard = ({
  slots,
  maxSlots,
  header,
  trailing = { kind: 'none' },
  topSlot,
  analyticsMode,
  analyticsRoomId = null,
}: SuspectBoardProps) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const showWantedTile = trailing.kind === 'wanted' && slots.length < maxSlots;
  const showRecruitTile = trailing.kind === 'recruit' && slots.length < maxSlots;
  const cellCount = slots.length + (showWantedTile || showRecruitTile ? 1 : 0);
  const cols = gridColsFor(Math.max(cellCount, 1));

  const editing = editingIndex !== null ? slots[editingIndex] : null;

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col gap-3">
      {topSlot !== undefined && <div className="flex justify-center">{topSlot}</div>}

      <header className="flex items-end gap-3 px-2">
        <HandKicker
          size={36}
          rot={-2}
          color="var(--color-magenta)"
          className="[text-shadow:0_1px_0_rgba(0,0,0,0.2)]"
        >
          {header}
        </HandKicker>
        <PhaseTag size={12} tracking={0.18} color="rgba(251,243,223,.65)" className="pb-1">
          {slots.length} / {maxSlots}
        </PhaseTag>
      </header>

      <ul
        className="grid min-h-0 flex-1 content-start gap-4 overflow-y-auto overflow-x-hidden px-2 pt-2 pb-4"
        style={{
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridAutoRows: 'max-content',
          scrollbarGutter: 'stable both-edges',
        }}
      >
        {slots.map((slot, index) => (
          <li key={slot.id} className="flex justify-center">
            <SlotCard
              index={index}
              slot={slot}
              onOpenEditor={slot.onMugshotChange ? () => setEditingIndex(index) : undefined}
            />
          </li>
        ))}
        {showRecruitTile && trailing.kind === 'recruit' && (
          <li key="recruit-next" className="flex justify-center">
            <RecruitTile onClick={trailing.onRecruit}>{trailing.label}</RecruitTile>
          </li>
        )}
        {showWantedTile && (
          <li key="wanted" className="flex justify-center">
            <WantedTile />
          </li>
        )}
      </ul>

      {editing?.onMugshotChange && (
        <MugshotEditor
          config={editing.mugshot}
          playerName={editing.name}
          onChange={editing.onMugshotChange}
          onNameChange={editing.onNameChange}
          onClose={() => setEditingIndex(null)}
          analyticsMode={analyticsMode}
          analyticsRoomId={analyticsRoomId}
        />
      )}
    </div>
  );
};
